import type {
  InvoicesListResponse,
  InvoicesSortField,
  InvoiceDetail,
  InvoiceMetadataUpdate,
  InvoiceCustomerChange,
  InvoiceLineItemCreate,
  InvoiceLineItemUpdate,
  ExcludedTicketLineItem,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface FetchInvoicesParams {
  page: number;             // 1-based (API convention)
  pageSize: number;
  sort?: InvoicesSortField; // default: 'number'
  order?: 'asc' | 'desc';   // default: 'desc'
  search?: string;
  unpaidOnly?: boolean;
  type?: 'scheduled' | 'standard';  // omit when both checked = no filter
  dueFrom?: string;         // YYYY-MM-DD
  dueTo?: string;           // YYYY-MM-DD
  includeDeleted?: boolean; // admin-only: include deactivated records
  signal?: AbortSignal;
}

/**
 * Fetch a page of invoices from the back end. Mirrors the envelope shape
 * of `searchWorkItemHistory` — items + pagination metadata.
 */
export async function fetchInvoices(
  params: FetchInvoicesParams,
): Promise<InvoicesListResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.search) qs.set('search', params.search);
  if (params.unpaidOnly) qs.set('unpaidOnly', 'true');
  if (params.type) qs.set('type', params.type);
  if (params.dueFrom) qs.set('dueFrom', params.dueFrom);
  if (params.dueTo) qs.set('dueTo', params.dueTo);
  if (params.includeDeleted) qs.set('includeDeleted', 'true');

  const response = await fetch(`${API_BASE_URL}/invoices?${qs}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch invoices: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: 1,
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    },
  };
}

// ---------------------------------------------------------------------------
// Invoice detail CRUD
// ---------------------------------------------------------------------------

export async function fetchInvoiceDetail(
  invoiceNumber: string,
  signal?: AbortSignal,
): Promise<InvoiceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch invoice ${invoiceNumber}: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

export async function updateInvoiceMetadata(
  invoiceNumber: string,
  updates: InvoiceMetadataUpdate,
  signal?: AbortSignal,
): Promise<InvoiceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
      signal,
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to update invoice: ${response.status}`);
  }
  return response.json();
}

/**
 * Result of a customer change: the refreshed invoice detail, plus an optional
 * `ticketWarning`. The invoice reassignment is the primary op; the linked-ticket
 * reassignment (when opted into) is best-effort — if it fails, the invoice change
 * still lands and `ticketWarning` carries a message for the user.
 */
export type ChangeInvoiceCustomerResult = InvoiceDetail & {
  ticketWarning?: string | null;
};

/**
 * Reassign the invoice to a different customer. The back end updates
 * RepairShopr first (source of truth), then mirrors the change into
 * Dataverse. When `alsoUpdateTicket` is set and the invoice is linked to an
 * RS ticket, the ticket + its DV job are reassigned to the same customer too
 * (best-effort — see `ticketWarning`).
 *
 * Rejected (400) for paid invoices — those must be corrected in RepairShopr
 * by a manager.
 */
export async function changeInvoiceCustomer(
  invoiceNumber: string,
  body: InvoiceCustomerChange,
  signal?: AbortSignal,
): Promise<ChangeInvoiceCustomerResult> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}/customer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to change customer: ${response.status}`);
  }
  return response.json();
}

export async function addInvoiceLineItem(
  invoiceNumber: string,
  item: InvoiceLineItemCreate,
  signal?: AbortSignal,
): Promise<InvoiceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}/line-items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
      signal,
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to add line item: ${response.status}`);
  }
  return response.json();
}

export async function updateInvoiceLineItem(
  invoiceNumber: string,
  lineItemId: number,
  updates: InvoiceLineItemUpdate,
  signal?: AbortSignal,
): Promise<InvoiceDetail> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}/line-items/${lineItemId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
      signal,
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to update line item: ${response.status}`);
  }
  return response.json();
}

export async function deleteInvoiceLineItem(
  invoiceNumber: string,
  lineItemId: number,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}/line-items/${lineItemId}`,
    { method: 'DELETE', signal },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to delete line item: ${response.status}`);
  }
}

export async function deleteInvoice(
  invoiceNumber: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}`,
    { method: 'DELETE', signal },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to delete invoice: ${response.status}`);
  }
}

export async function printInvoice(
  invoiceNumber: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/${encodeURIComponent(invoiceNumber)}/print`,
    { method: 'POST', signal },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to print invoice: ${response.status}`);
  }
}

/**
 * Re-pull an invoice from RepairShopr into Dataverse (RS → DV). Queues an
 * orchestrator job that fetches from RS and upserts the cr001_invoiceses row.
 *
 * ASYNC: resolves with the 202 body `{ messageId, status: "accepted" }` — the
 * DV row is NOT updated by the time this returns. Callers should re-fetch the
 * invoice a few seconds later to see the refreshed data.
 */
export async function syncInvoiceFromRs(
  invoiceNumber: string,
  signal?: AbortSignal,
): Promise<{ messageId: string; status: string }> {
  const response = await fetch(
    `${API_BASE_URL}/invoices/by-number/${encodeURIComponent(invoiceNumber)}/sync`,
    { method: 'POST', signal },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to refresh invoice from RepairShopr: ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Invoice creation (orchestrator)
// ---------------------------------------------------------------------------

export interface CreateInvoiceParams {
  rsCustomerId: number;
  rsTicketId?: number;
  dvJobGuid?: string;
  /**
   * Ticket line items to OMIT from the new invoice. RS auto-copies every
   * unconverted ticket line item; the orchestrator deletes the matching
   * invoice line items for each entry here (matched by name/description/
   * quantity/price). Pass undefined or [] to keep every ticket item.
   */
  excludedTicketLineItems?: ExcludedTicketLineItem[];
}

export async function createInvoice(
  params: CreateInvoiceParams,
  signal?: AbortSignal,
): Promise<{ messageId: string; status: string }> {
  const response = await fetch(`${API_BASE_URL}/invoices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to create invoice: ${response.status}`);
  }
  return response.json();
}
